import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { LABEL_STATUSES, type Label, type LabelStatus } from '@trivio/contracts';
import { Button } from '@tervior-tech/tervior-shared-ui-react';
import { useDashboardSessionKey } from '../../app/DashboardSessionKeyContext';
import { useLabels, type LabelFilters } from './useLabels';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Alert } from '../../components/ui/Alert';
import { LoadingState } from '../../components/ui/Spinner';
import { Input } from '../../components/ui/Input';
import { Label as FormLabel } from '../../components/ui/Label';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { ApiProblemError, API_DISPLAY_BASE_URL, cancelLabel, getLabelDocument } from '../../lib/api-client';
import { labelFailureReasonPt, labelStatusBadgeVariant, labelStatusPt, providerIdPt } from '../../utils/enums-ptbr';
import { formatCentsToBRL } from '../../utils/money';

/** Triggers a normal browser download for the fetched PDF — no dedicated "save" API here (this is a real SPA route, not a sandboxed page), so `URL.createObjectURL` + a throwaway `<a download>` is the standard pattern. */
function triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = window.document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  window.document.body.appendChild(anchor);
  anchor.click();
  window.document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

function extractProblemMessage(err: unknown, fallback: string): string {
  return err instanceof ApiProblemError ? (err.problem.detail ?? err.problem.title) : fallback;
}

/** `/labels` (TTP-244) — paginated, filterable table of every label the org has issued, across every connected provider. */
export function LabelsPage() {
  const { key } = useDashboardSessionKey();
  const apiKey = key?.key ?? null;

  const [statusFilter, setStatusFilter] = useState<LabelStatus | ''>('');
  const [externalRefInput, setExternalRefInput] = useState('');
  const [externalRefFilter, setExternalRefFilter] = useState('');

  const filters: LabelFilters = {
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(externalRefFilter ? { external_ref: externalRefFilter } : {}),
  };

  const { labels, loading, loadingMore, error, hasMore, loadMore, refetch } = useLabels(apiKey, filters);

  const [actionError, setActionError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<Label | null>(null);
  const [canceling, setCanceling] = useState(false);

  function handleFilterSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setExternalRefFilter(externalRefInput.trim());
  }

  async function handleDownload(label: Label) {
    if (!apiKey) return;
    setActionError(null);
    setDownloadingId(label.label_id);
    try {
      const document_ = await getLabelDocument(apiKey, label.label_id, 'pdf');
      triggerBrowserDownload(document_.blob, `etiqueta-${label.label_id}.pdf`);
    } catch (err) {
      setActionError(extractProblemMessage(err, 'Não foi possível baixar o PDF agora.'));
    } finally {
      setDownloadingId(null);
    }
  }

  async function handleConfirmCancel() {
    if (!apiKey || !cancelTarget) return;
    setCanceling(true);
    setActionError(null);
    try {
      await cancelLabel(apiKey, cancelTarget.label_id);
      setCancelTarget(null);
      refetch();
    } catch (err) {
      setActionError(extractProblemMessage(err, 'Não foi possível cancelar a etiqueta agora.'));
    } finally {
      setCanceling(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-medium text-foreground">Etiquetas</h1>
        <p className="text-sm text-muted-foreground">
          Etiquetas emitidas pela sua organização, de todos os provedores conectados.
        </p>
      </div>

      <form
        onSubmit={handleFilterSubmit}
        className="flex flex-col gap-3 border border-border p-4 sm:flex-row sm:items-end sm:gap-4"
      >
        <div className="flex flex-col gap-2">
          <FormLabel htmlFor="labels-status-filter">Status</FormLabel>
          <select
            id="labels-status-filter"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as LabelStatus | '')}
            className="rounded-none border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
          >
            <option value="">Todos</option>
            {LABEL_STATUSES.map((status) => (
              <option key={status} value={status}>
                {labelStatusPt(status)}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-1 flex-col gap-2">
          <FormLabel htmlFor="labels-external-ref-filter">Referência externa</FormLabel>
          <Input
            id="labels-external-ref-filter"
            value={externalRefInput}
            onChange={(event) => setExternalRefInput(event.target.value)}
            placeholder="Ex.: pedido-1234"
          />
        </div>
        <Button type="submit" variant="outline">
          Filtrar
        </Button>
      </form>

      {actionError ? (
        <Alert variant="error" title="Ação não concluída">
          {actionError}
        </Alert>
      ) : null}
      {error ? (
        <Alert variant="error" title="Não foi possível carregar as etiquetas">
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <LoadingState label="Carregando etiquetas…" />
      ) : labels.length === 0 ? (
        <Card className="flex flex-col gap-3 p-6">
          <p className="text-sm text-foreground">Nenhuma etiqueta ainda.</p>
          <p className="text-sm text-muted-foreground">Emita a primeira etiqueta diretamente pela API pública:</p>
          <pre className="overflow-x-auto rounded-none border border-border bg-muted/30 p-4 font-mono text-xs leading-relaxed text-foreground">
            <code>{`curl -X POST '${API_DISPLAY_BASE_URL}/v1/labels' \\
  -H 'Authorization: Bearer <sua_api_key>' \\
  -H 'Idempotency-Key: <chave_unica>' \\
  -H 'Content-Type: application/json' \\
  -d '{ "offer_id": "...", "shipper": { ... }, "recipient": { ... } }'`}</code>
          </pre>
          <p className="text-xs text-muted-foreground">
            Pegue sua chave em{' '}
            <Link to="/api-keys" className="font-medium text-primary underline-offset-4 hover:underline">
              API keys
            </Link>
            .
          </p>
        </Card>
      ) : (
        <>
          <div className="hidden overflow-x-auto border border-border lg:block">
            <table className="w-full border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-border text-xs font-medium uppercase tracking-widest text-muted-foreground">
                  <th className="px-4 py-3">Criada em</th>
                  <th className="px-4 py-3">Referência</th>
                  <th className="px-4 py-3">Provedor</th>
                  <th className="px-4 py-3">Preço</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Rastreio</th>
                  <th className="px-4 py-3">Ações</th>
                </tr>
              </thead>
              <tbody>
                {labels.map((label) => (
                  <tr key={label.label_id} className="border-b border-border/60 last:border-b-0">
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                      {new Date(label.created_at).toLocaleString('pt-BR')}
                    </td>
                    <td className="px-4 py-3">{label.external_ref ?? '—'}</td>
                    <td className="px-4 py-3">
                      {providerIdPt(label.provider)} · {label.carrier} · {label.service}
                    </td>
                    <td className="px-4 py-3">{formatCentsToBRL(label.price_cents)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <Badge variant={labelStatusBadgeVariant(label.status)}>{labelStatusPt(label.status)}</Badge>
                        {label.status === 'failed' && label.failure_reason ? (
                          <span className="text-xs text-accent">{labelFailureReasonPt(label.failure_reason)}</span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{label.tracking_code ?? '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={label.status !== 'ready' || downloadingId === label.label_id}
                          onClick={() => handleDownload(label)}
                        >
                          {downloadingId === label.label_id ? 'Baixando…' : 'PDF'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={label.status === 'canceled' || label.status === 'failed'}
                          onClick={() => setCancelTarget(label)}
                        >
                          Cancelar
                        </Button>
                        <Link
                          to={`/labels/${label.label_id}`}
                          className="inline-flex items-center px-2 text-sm font-medium text-primary underline-offset-4 hover:underline"
                        >
                          Ver rastreio
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-4 lg:hidden">
            {labels.map((label) => (
              <Card key={label.label_id} className="flex flex-col gap-3 p-4">
                <div className="flex items-center justify-between gap-2">
                  <Badge variant={labelStatusBadgeVariant(label.status)}>{labelStatusPt(label.status)}</Badge>
                  <span className="font-mono text-xs text-muted-foreground">
                    {new Date(label.created_at).toLocaleDateString('pt-BR')}
                  </span>
                </div>
                <div className="text-sm text-foreground">
                  {providerIdPt(label.provider)} · {label.carrier} · {label.service}
                </div>
                <div className="text-sm text-muted-foreground">Ref.: {label.external_ref ?? '—'}</div>
                <div className="text-lg font-medium text-foreground">{formatCentsToBRL(label.price_cents)}</div>
                {label.status === 'failed' && label.failure_reason ? (
                  <p className="text-xs text-accent">{labelFailureReasonPt(label.failure_reason)}</p>
                ) : null}
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={label.status !== 'ready' || downloadingId === label.label_id}
                    onClick={() => handleDownload(label)}
                  >
                    {downloadingId === label.label_id ? 'Baixando…' : 'Baixar PDF'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={label.status === 'canceled' || label.status === 'failed'}
                    onClick={() => setCancelTarget(label)}
                  >
                    Cancelar
                  </Button>
                  <Link
                    to={`/labels/${label.label_id}`}
                    className="inline-flex items-center text-sm font-medium text-primary underline-offset-4 hover:underline"
                  >
                    Ver rastreio
                  </Link>
                </div>
              </Card>
            ))}
          </div>

          {hasMore ? (
            <Button type="button" variant="outline" onClick={loadMore} disabled={loadingMore} className="self-center">
              {loadingMore ? 'Carregando…' : 'Carregar mais'}
            </Button>
          ) : null}
        </>
      )}

      <ConfirmDialog
        open={cancelTarget !== null}
        title="Cancelar etiqueta"
        description={
          cancelTarget
            ? `Tem certeza que deseja cancelar a etiqueta de ${providerIdPt(cancelTarget.provider)} (${cancelTarget.tracking_code ?? cancelTarget.label_id})? Essa ação não pode ser desfeita.`
            : ''
        }
        confirmLabel="Cancelar etiqueta"
        cancelLabel="Voltar"
        loading={canceling}
        onConfirm={handleConfirmCancel}
        onCancel={() => setCancelTarget(null)}
      />
    </div>
  );
}
